import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Container, Input, Row, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import DeleteModal from "../../../Components/Common/DeleteModal";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { toast } from "react-toastify";
import {
  addScore,
  updateScore,
  deleteScores,
  activateDeactivateScore,
  fetchAllScores, 
  fetchScoreTableColumnNames,
  fetchTranslation, 
  updateUserPreference, 
  getDifficultyLevel,
  getQuestionTypes
} from "../../../slices/thunks";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import AddSubTopicsModalForm from "../../../Components/Common/AddTopicsModalForm/AddSubTopicsModalForm";
import CommonAddModal from "../../../Components/Common/Modal/CommonAddModal";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import { getAuthUser } from "../../../helpers/utils";
import usePermissions from "../../../Components/Hooks/usePermissions";
import AddManageScoreForm from "../../../Components/Common/AddManageScoreForm/AddManageScoreForm";

const labels = [
  {
    id: 1,
    labelName: "Question Type",
    fieldName: "questiontype-dropdown",
    name: "QuestionTypeID",
    value: "questionTypeId",
    required: true,
    errorMessage: "Please Select Question Type",
  },
  {
    id: 2,
    labelName: "Difficulty Level",
    fieldName: "difficulty-dropdown",
    name: "DifficultyLevel",
    value: "difficultyId",
    required: true,
    errorMessage: "Please Select Difficulty Level",
  },
  {
    id: 3,
    labelName: "Score Value",
    fieldName: "number-box", 
    name: "Mark",
    value: "mark",
    required: true,
    errorMessage: "Please Enter Score Value",
  },
];

const actionConfig = {
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit",
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete",
  },
};

function Score() { 
  const dispatch = useDispatch();
  const { user } = getAuthUser();
  const { t, i18n } = useTranslation();
  
  const [show, setShow] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOrEdit, setIsAddOrEdit] = useState("isAdd");
  const [selectedRow, setSelectedRow] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [lastTranslatedField, setLastTranslatedField] = useState(null);

  const { scores, loading, headerload, error, tableColumnNames, module, translationLoading } = useSelector((state) => ({
    scores: state.Score.scores, 
    loading: state.Score.loading,
    headerload: state.Score.headerload,
    error: state.Score.error,
    tableColumnNames: state.Score.tableColumnNames,
    module: state.Module.module, 
    translationLoading: state.Module.translationLoading, 
  }));


  // Compute permission
    const permission = (() => {
        // moduleId: 3 (Administration), pageId: 20 (Manage Score)
        return usePermissions(3, 20);
    })();

  useEffect(  () => {
    dispatch(fetchScoreTableColumnNames({ module: "SCORE" }))
    dispatch(fetchAllScores());
    dispatch(getQuestionTypes())
    dispatch(getDifficultyLevel())
  }, [dispatch]);

  const onChangeHandler = (e) => {
    setSelectedRow((preValue) => ({
      ...preValue,
      [e.target.name]: e.target.value,
    }));
  };

  const handleClassesClicks = (value) => {
    setShow(true);
    setIsDisabled(false); // Allow Formik to control initial button state for "Add New"
    setIsAddOrEdit(value);
    setSelectedRow({});
  };

  const onEditClickHandler = (rowData, value) => {
      setSelectedRow(rowData);
      setIsDisabled(false);
      setIsAddOrEdit(value);
      setShow(true);
  }

   const onTranslateClickHandler = (targetField, textToTranslate, formikSetFieldValue, sourceFieldName) => {
      if (!textToTranslate || textToTranslate.trim() === "") {
        toast.error(t("EnterTextToTranslate"));
        return;
      }
      setLastTranslatedField(sourceFieldName); 
  
      dispatch(fetchTranslation({ LangText: textToTranslate }))
        .unwrap() 
        .then(translatedText => {
          formikSetFieldValue(targetField, translatedText);
        })
        .catch(error => {
          console.error("Translation error:", error);
          toast.error(t("TranslationFailed"));
        })
        .finally(() => {
          setLastTranslatedField(null); 
        });
    };


  // Handler for single delete confirmation
  const handleConfirmSingleDelete = () => {
    if (!permission.CanDelete) {
      toast.error(t("NoPermissionToDelete"));
      setDeleteModal(false);
      return;
    }
    dispatch(deleteScores(selectedIds)).unwrap()
        .then(() => {
          setSelectedIds([]); 
        })
        .catch((error) => {
          console.error("Delete error:", error);
        })
    setDeleteModal(false);
  };

   const onSubmitHandler = async (values, { resetForm, setSubmitting }) => {
      try {
        const payload = {
          QuestionTypeID: values.QuestionTypeID,
          DifficultyLevel: values.DifficultyLevel,
          Mark: values.Mark,
        };

  
        if (isAddOrEdit === 'isEdit') {
          payload.ID = selectedRow.ID;
          dispatch(updateScore(payload));
        } else {
          dispatch(addScore(payload));
        }
        setShow(false); 
        resetForm();    
      } catch (error) {
        console.error("Score submission error:", error);
        toast.error(t("FailedToSubmitScore"));
      } finally {
        setSubmitting(false); 
      }
    };

  const handleSave = async () => {
    const preferencesPayload = {
      UserId: user.UserId,
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ColumnId] = selectedColumns.includes(column.ColumnId);
        return acc;
      }, {}),
    };

    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(fetchScoreTableColumnNames({module: "SCORE"}));

      setModalOpen(false);
    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };
  
  const onActiveOrDeactiveChange = (item, e) => {
    if (!permission.CanEdit) {
      toast.error(t("NoPermissionToEdit"));
      return;
    }
      const scoreObj = {}; 
      scoreObj['ID'] = item.ID;
      scoreObj['IsActive'] = !item.IsActive;
      // TableName might not be needed if activateDeactivateScore is specific
    //   dispatch(activateDeactivateScore(scoreObj));
  }

  const onDeleteClickHandler = rowData => {
    if (!permission.CanDelete) {
      toast.error(t("NoPermissionToDelete"));
      return;
    }
    setSelectedIds([rowData.ID]);
    setDeleteModal(true);
  }


  return (
    <React.Fragment>
    
      <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t("Manage Scores")} />
          </Col>
        </Row>
        <div className="table-responsive">
          <Card>
            <CardHeader>
                <Row className="g-4">
                    <Col className="col-sm-auto">
                      <div>
                        <Button
                          className="btn btn-soft-danger ms-2 me-2"
                          onClick={() => setDeleteModal(true)}
                          disabled={selectedIds.length === 0}
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>
                        {permission.CanAdd && (
                          <Button
                            color="secondary"
                            className="add-btn me-1"
                            onClick={() => handleClassesClicks("isAdd")}
                            id="create-btn"
                            disabled={loading}
                          >
                            <i className="ri-add-line align-bottom me-1 ms-1"></i>
                            {t("AddNew")}
                          </Button>
                        )}
                      </div>
                    </Col>
                    <Col className="col-sm">
                      <div className="d-flex justify-content-sm-end">
                        <Button
                          className="btn btn-soft"
                          onClick={() => setModalOpen(true)}
                          disabled={loading}
                        >
                          <i className="ri-filter-3-line"></i>
                        </Button>
                        <div className="search-box ms-2 me-2">
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={t("Search")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>
                      </div>
                    </Col>
                  </Row>
            </CardHeader>
            <CardBody>
              {loading || headerload ? (
                <div className="d-flex justify-content-center align-items-center">
                <Spinner style={{ height: "3rem", width: "3rem" }} className="me-2">
                  Loading...
                </Spinner>
                </div>
              ) : (
                <div className="listjs-table" id="customerList">
                   <CommonDataTable
                      data={scores || []}
                      tableColumnNames={tableColumnNames || []}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      actionConfig={actionConfig}
                      permission={permission}
                    />
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </Container>  
      </div>      

        {/* Add manage score  */}
         <CommonAddModal
            show={show}
            setShow={setShow}
            onSubmitHandler={onSubmitHandler}
            labels={labels}
            isAddOrEdit={isAddOrEdit}
            selectedItemData={selectedRow} 
            FormBody={AddManageScoreForm} 
            FormBodyProps={{ 
              onTranslateClickHandler, 
              translationLoading,
              lastTranslatedField,
              setParentSubmitDisabled: setIsDisabled, 
            }}
            title={isAddOrEdit === "isAdd" ? t("Add New Score") : t("Edit Score")}
            size="lg" 
            isDisabled={isDisabled} 
         />

        {/* Single Delete Confirmation Modal  */}
        <DeleteModal
          showDelete={deleteModal}
          setShowDelete={setDeleteModal}
          onDeleteClick={handleConfirmSingleDelete}
          setSelectedIds={setSelectedIds}
          description={"Are you sure you want to delete this record?"}      
        />

        {/* ColumnSelectionModal */}
        <ColumnSelectionModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(!modalOpen)}
          columns={tableColumnNames}
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
          handleSave={handleSave}
        />

    </React.Fragment>
  );
}

export default Score;
